import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Megaphone, Link, Clock, CheckCircle, XCircle, Loader2, FileText, Music, Video, Image, X, Upload, Eye, Download, RefreshCw, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { uploadFileToS3, getPresignedDownloadUrl, extractFileKey, getAssetUrl } from '@/utils/s3Upload';

// File size limits in bytes
const FILE_LIMITS = {
  image: 5 * 1024 * 1024,      // 5 MB
  pdf: 10 * 1024 * 1024,       // 10 MB
  audio: 25 * 1024 * 1024,     // 25 MB
  video: 50 * 1024 * 1024,     // 50 MB
};

const MAX_FILES = 6;

// Get file type from MIME type
const getFileType = (mimeType) => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  return null;
};

// Get file type icon
const getFileIcon = (type) => {
  switch (type) {
    case 'image': return <Image className="h-4 w-4" />;
    case 'pdf': return <FileText className="h-4 w-4" />;
    case 'audio': return <Music className="h-4 w-4" />;
    case 'video': return <Video className="h-4 w-4" />;
    default: return <FileText className="h-4 w-4" />;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Render attachment preview in list
const renderAttachmentPreview = (attachment) => {
  // Use presigned URL if available
  const url = attachment.presigned_url || attachment.url;
  
  switch (attachment.type) {
    case 'image':
      return (
        <img src={url} alt={attachment.filename} className="w-full h-full object-cover" />
      );
    case 'pdf':
      return (
        <div className="w-full h-full flex items-center justify-center bg-red-50">
          <FileText className="h-8 w-8 text-red-500" />
        </div>
      );
    case 'audio':
      return (
        <div className="w-full h-full flex items-center justify-center bg-purple-50">
          <Music className="h-8 w-8 text-purple-500" />
        </div>
      );
    case 'video':
      return (
        <div className="w-full h-full flex items-center justify-center bg-blue-50">
          <Video className="h-8 w-8 text-blue-500" />
        </div>
      );
    default:
      return null;
  }
};

// File picker component - extracted for reuse
const FilePickerSection = ({
  isEdit = false,
  pendingFiles,
  existingAttachments,
  onFilesChange,
  onRemovePendingFile,
  onRemoveExistingAttachment
}) => {
  const totalFiles = pendingFiles.length + existingAttachments.length;
  
  return (
    <div>
      <Label>Attachments (max {MAX_FILES} files)</Label>
      <p className="text-xs mb-2" style={{ color: '#5C635A' }}>
        Images (5MB), PDFs (10MB), Audio (25MB), Video (50MB)
      </p>
      
      {/* Existing attachments (edit mode) */}
      {existingAttachments.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium mb-1" style={{ color: '#163329' }}>Current attachments:</p>
          <div className="flex flex-wrap gap-2">
            {existingAttachments.map((att, idx) => (
              <div key={idx} className="relative group">
                <div className="w-16 h-16 rounded-lg overflow-hidden border" style={{ borderColor: '#E3E1DB' }}>
                  {renderAttachmentPreview(att)}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onRemoveExistingAttachment(idx)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <p className="text-xs truncate w-16 text-center mt-1" style={{ color: '#5C635A' }}>
                  {att.filename}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pending files to upload */}
      {pendingFiles.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium mb-1" style={{ color: '#163329' }}>
            {isEdit ? 'New files to add:' : 'Files to upload:'}
          </p>
          <div className="flex flex-wrap gap-2">
            {pendingFiles.map((fileObj, idx) => (
              <div key={idx} className="relative group">
                <div className="w-16 h-16 rounded-lg overflow-hidden border flex items-center justify-center" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA' }}>
                  {fileObj.preview ? (
                    <img src={fileObj.preview} alt={fileObj.name} className="w-full h-full object-cover" />
                  ) : (
                    getFileIcon(fileObj.type)
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onRemovePendingFile(idx)}
                >
                  <X className="h-3 w-3" />
                </Button>
                <p className="text-xs truncate w-16 text-center mt-1" style={{ color: '#5C635A' }}>
                  {fileObj.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* File input */}
      {totalFiles < MAX_FILES && (
        <div className="mt-2">
          <Input
            type="file"
            accept="image/*,.pdf,audio/*,video/*"
            multiple
            onChange={onFilesChange}
            data-testid="announcement-files-input"
          />
        </div>
      )}
      
      {totalFiles >= MAX_FILES && (
        <p className="text-xs text-amber-600 mt-2">
          Maximum {MAX_FILES} files reached
        </p>
      )}
    </div>
  );
};

const TeacherAnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [replacingAttachmentIndex, setReplacingAttachmentIndex] = useState(null); // Track which attachment is being replaced
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link_url: ''
  });
  
  // Attachments state - files to upload
  const [pendingFiles, setPendingFiles] = useState([]);
  // Existing attachments (for edit mode)
  const [existingAttachments, setExistingAttachments] = useState([]);
  
  // Reorder state
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements/my-announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setIsLoading(false);
    }
  };

  // Reorder announcements
  const handleReorder = async (announcementId, direction) => {
    const currentIndex = announcements.findIndex(a => a.id === announcementId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= announcements.length) return;
    
    setIsReordering(true);
    
    // Create new order array
    const newAnnouncements = [...announcements];
    const [removed] = newAnnouncements.splice(currentIndex, 1);
    newAnnouncements.splice(newIndex, 0, removed);
    
    // Update local state optimistically
    setAnnouncements(newAnnouncements);
    
    // Build the reorder payload
    const announcementOrders = newAnnouncements.map((a, idx) => ({
      id: a.id,
      order: idx + 1
    }));
    
    try {
      await api.patch('/announcements/reorder', { announcements: announcementOrders });
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to reorder announcements');
      // Revert on error
      fetchAnnouncements();
    } finally {
      setIsReordering(false);
    }
  };

  // Direct order input
  const handleOrderInput = async (announcementId, newOrderStr) => {
    const newOrder = parseInt(newOrderStr, 10);
    if (isNaN(newOrder) || newOrder < 1 || newOrder > announcements.length) {
      toast.error(`Order must be between 1 and ${announcements.length}`);
      return;
    }
    
    const currentIndex = announcements.findIndex(a => a.id === announcementId);
    if (currentIndex === -1) return;
    
    const targetIndex = newOrder - 1;
    if (currentIndex === targetIndex) return;
    
    setIsReordering(true);
    
    // Reorder array
    const newAnnouncements = [...announcements];
    const [removed] = newAnnouncements.splice(currentIndex, 1);
    newAnnouncements.splice(targetIndex, 0, removed);
    
    setAnnouncements(newAnnouncements);
    
    // Build the reorder payload
    const announcementOrders = newAnnouncements.map((a, idx) => ({
      id: a.id,
      order: idx + 1
    }));
    
    try {
      await api.patch('/announcements/reorder', { announcements: announcementOrders });
      toast.success('Order updated');
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to update order');
      fetchAnnouncements();
    } finally {
      setIsReordering(false);
    }
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = pendingFiles.length + existingAttachments.length;
    
    if (currentCount + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed per announcement`);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const fileType = getFileType(file.type);
      
      if (!fileType) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }

      const maxSize = FILE_LIMITS[fileType];
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large (max ${formatFileSize(maxSize)} for ${fileType})`);
        return;
      }

      // Create preview for images
      const fileObj = {
        file,
        type: fileType,
        name: file.name,
        size: file.size,
        preview: null
      };

      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPendingFiles(prev => prev.map(f => 
            f.name === file.name ? { ...f, preview: reader.result } : f
          ));
        };
        reader.readAsDataURL(file);
      }

      validFiles.push(fileObj);
    });

    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
    }

    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
    }

    // Reset input
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = (index) => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      link_url: ''
    });
    setPendingFiles([]);
    setExistingAttachments([]);
    setSelectedAnnouncement(null);
    setUploadProgress('');
  };

  const uploadFiles = async () => {
    const uploadedAttachments = [];
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const fileObj = pendingFiles[i];
      setUploadProgress(`Uploading ${i + 1}/${pendingFiles.length}: ${fileObj.name}`);
      
      try {
        const { promise } = uploadFileToS3(fileObj.file, 'announcements/');
        const url = await promise;
        uploadedAttachments.push({
          url,
          type: fileObj.type,
          filename: fileObj.name,
          size_bytes: fileObj.size
        });
      } catch (error) {
        console.error(`Failed to upload ${fileObj.name}:`, error);
        throw new Error(`Failed to upload ${fileObj.name}`);
      }
    }
    
    setUploadProgress('');
    return uploadedAttachments;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Upload all pending files
      let attachments = [];
      if (pendingFiles.length > 0) {
        attachments = await uploadFiles();
        toast.success(`${attachments.length} file(s) uploaded`);
      }

      await api.post('/announcements/', {
        title: formData.title,
        description: formData.description,
        attachments: attachments,
        link_url: formData.link_url
      });

      toast.success('Announcement submitted for approval');
      setShowCreate(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      toast.error(error.message || error.response?.data?.detail || 'Failed to create announcement');
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedAnnouncement) return;
    setIsSubmitting(true);

    try {
      // Upload new pending files
      let newAttachments = [];
      if (pendingFiles.length > 0) {
        newAttachments = await uploadFiles();
        toast.success(`${newAttachments.length} file(s) uploaded`);
      }

      // Combine existing and new attachments
      const allAttachments = [...existingAttachments, ...newAttachments];

      await api.put(`/announcements/${selectedAnnouncement.id}`, {
        title: formData.title,
        description: formData.description,
        attachments: allAttachments,
        link_url: formData.link_url
      });

      toast.success('Announcement updated (resubmitted for approval)');
      setShowEdit(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to update announcement:', error);
      toast.error(error.message || error.response?.data?.detail || 'Failed to update announcement');
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  // View attachment - opens file based on type (similar to LessonViewPage.jsx handleSecureFileView)
  const handleViewAttachment = async (attachment) => {
    try {
      // Use presigned_url if available, otherwise compute it
      let fileUrl = attachment.presigned_url;
      if (!fileUrl) {
        const fileKey = extractFileKey(attachment.url);
        if (fileKey) {
          const result = await getPresignedDownloadUrl(fileKey, null);
          fileUrl = result.download_url;
        } else {
          fileUrl = attachment.url;
        }
      }

      if (!fileUrl) {
        toast.error('Failed to get file URL');
        return;
      }

      const isPdf = attachment.type === 'pdf' || attachment.url?.endsWith('.pdf');
      const isAudio = attachment.type === 'audio';
      const isImage = attachment.type === 'image';

      if (isPdf) {
        // Open PDF in Google Docs viewer
        window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=false`, '_blank');
      } else if (isImage) {
        // Open image directly in new tab
        window.open(fileUrl, '_blank');
      } else {
        // For video/audio, open in new tab with a simple player
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${attachment.filename || 'Media File'}</title>
              <style>
                body { margin: 0; background: #000; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
                video, audio { max-width: 100%; max-height: 100vh; }
              </style>
            </head>
            <body>
              <${isAudio ? 'audio' : 'video'} controls autoplay preload="metadata" src="${fileUrl}"></${isAudio ? 'audio' : 'video'}>
            </body>
            </html>
          `);
        }
      }
    } catch (error) {
      console.error('Error viewing attachment:', error);
      toast.error('Failed to load file');
    }
  };

  // Download attachment - forces file download using download=true flag
  const handleDownloadAttachment = async (attachment) => {
    try {
      const fileKey = extractFileKey(attachment.url);
      if (!fileKey) {
        toast.error('Invalid file URL');
        return;
      }

      // Get presigned URL with download=true flag
      const result = await getPresignedDownloadUrl(fileKey, null, true);
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download file');
    }
  };

  // Replace attachment - upload new file and update announcement
  const handleReplaceAttachment = async (announcementId, attachmentIndex, file) => {
    if (!file) return;

    const fileType = getFileType(file.type);
    if (!fileType) {
      toast.error('Unsupported file type');
      return;
    }

    const maxSize = FILE_LIMITS[fileType];
    if (file.size > maxSize) {
      toast.error(`File too large (max ${formatFileSize(maxSize)} for ${fileType})`);
      return;
    }

    setReplacingAttachmentIndex(attachmentIndex);
    setUploadProgress(`Replacing attachment...`);

    try {
      // Upload the new file
      const { promise } = uploadFileToS3(file, 'announcements/');
      const newUrl = await promise;

      // Find the announcement and build updated attachments array
      const announcement = announcements.find(a => a.id === announcementId);
      if (!announcement) {
        throw new Error('Announcement not found');
      }

      const updatedAttachments = [...(announcement.attachments || [])];
      updatedAttachments[attachmentIndex] = {
        url: newUrl,
        type: fileType,
        filename: file.name,
        size_bytes: file.size
      };

      // Update the announcement via PUT
      await api.put(`/announcements/${announcementId}`, {
        title: announcement.title,
        description: announcement.description,
        attachments: updatedAttachments,
        link_url: announcement.link_url
      });

      toast.success('Attachment replaced successfully');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to replace attachment:', error);
      toast.error(error.message || 'Failed to replace attachment');
    } finally {
      setReplacingAttachmentIndex(null);
      setUploadProgress('');
    }
  };

  const openEditDialog = (announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      description: announcement.description || '',
      link_url: announcement.link_url || ''
    });
    // Load existing attachments
    setExistingAttachments(announcement.attachments || []);
    // Handle legacy image_url field
    if (announcement.image_url && (!announcement.attachments || announcement.attachments.length === 0)) {
      setExistingAttachments([{
        url: announcement.image_url,
        type: 'image',
        filename: 'Legacy Image',
        size_bytes: 0
      }]);
    }
    setPendingFiles([]);
    setShowEdit(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/teacher/dashboard')}
              className="p-2"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 
                className="text-2xl sm:text-3xl font-bold"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                My Announcements
              </h1>
              <p className="text-sm" style={{ color: '#5C635A' }}>
                Create announcements to display on the landing page ribbon
              </p>
            </div>
          </div>
          
          <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full"
                style={{ backgroundColor: '#163329' }}
                data-testid="create-announcement-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Create Announcement
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., New Course Launch!"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of your announcement..."
                    rows={3}
                    data-testid="announcement-description-input"
                  />
                </div>
                
                <FilePickerSection
                  isEdit={false}
                  pendingFiles={pendingFiles}
                  existingAttachments={existingAttachments}
                  onFilesChange={handleFilesChange}
                  onRemovePendingFile={removePendingFile}
                  onRemoveExistingAttachment={removeExistingAttachment}
                />
                
                <div>
                  <Label htmlFor="link">Link URL (optional)</Label>
                  <Input
                    id="link"
                    type="url"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="announcement-link-input"
                  />
                  <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                    Users will be redirected here when clicking the announcement
                  </p>
                </div>
                
                {uploadProgress && (
                  <div className="p-2 rounded bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {uploadProgress}
                  </div>
                )}
                
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowCreate(false); resetForm(); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.title}
                    style={{ backgroundColor: '#163329' }}
                    data-testid="submit-announcement-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit for Approval'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="mb-6" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA' }}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Megaphone className="h-5 w-5 mt-0.5" style={{ color: '#163329' }} />
              <div>
                <p className="font-medium" style={{ color: '#163329' }}>How it works</p>
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  Create announcements with images, PDFs, audio, or video to promote your courses.
                  Once approved by admin, they will appear in the Teacher Announcements ribbon.
                  This is a free feature!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card className="p-12 text-center" style={{ borderColor: '#E3E1DB' }}>
            <Megaphone className="h-12 w-12 mx-auto mb-4" style={{ color: '#5C635A' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#163329' }}>
              No announcements yet
            </h3>
            <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
              Create your first announcement to showcase on the landing page
            </p>
            <Button
              onClick={() => setShowCreate(true)}
              style={{ backgroundColor: '#163329' }}
              className="rounded-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Announcement
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement, idx) => (
              <Card key={announcement.id} className="overflow-hidden" style={{ borderColor: '#E3E1DB' }} data-testid={`announcement-card-${announcement.id}`}>
                <div className="flex">
                  {/* Reorder controls column */}
                  <div className="flex flex-col items-center justify-center p-2 border-r" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA', minWidth: '60px' }}>
                    <input
                      type="number"
                      min="1"
                      max={announcements.length}
                      defaultValue={idx + 1}
                      key={`order-${announcement.id}-${idx}`}
                      className="w-10 h-7 text-center text-sm border rounded"
                      style={{ borderColor: '#E3E1DB' }}
                      onBlur={(e) => handleOrderInput(announcement.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      disabled={isReordering}
                      data-testid={`order-input-${announcement.id}`}
                    />
                    <GripVertical className="h-4 w-4 my-1" style={{ color: '#5C635A' }} />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => handleReorder(announcement.id, 'up')}
                      disabled={idx === 0 || isReordering}
                      data-testid={`move-up-${announcement.id}`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => handleReorder(announcement.id, 'down')}
                      disabled={idx === announcements.length - 1 || isReordering}
                      data-testid={`move-down-${announcement.id}`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    {/* Preview first image attachment or legacy image_url */}
                    {(announcement.attachments?.find(a => a.type === 'image') || announcement.image_url) && (
                      <div className="w-full h-32 overflow-hidden">
                        <img
                          src={announcement.attachments?.find(a => a.type === 'image')?.url || announcement.image_url}
                          alt={announcement.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle 
                          className="text-lg line-clamp-2"
                          style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                        >
                          {announcement.title}
                        </CardTitle>
                        {getStatusBadge(announcement.status)}
                      </div>
                    </CardHeader>
                <CardContent>
                  {announcement.description && (
                    <p className="text-sm line-clamp-2 mb-3" style={{ color: '#5C635A' }}>
                      {announcement.description}
                    </p>
                  )}
                  
                  {/* Per-attachment list with View, Download, Replace buttons */}
                  {announcement.attachments?.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#5C635A' }}>
                        <Upload className="h-3 w-3" />
                        <span>{announcement.attachments.length} attachment(s)</span>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {announcement.attachments.map((attachment, attIdx) => (
                          <div 
                            key={attIdx} 
                            className="p-2 rounded-lg border"
                            style={{ backgroundColor: '#F0EFEA', borderColor: '#E3E1DB' }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {getFileIcon(attachment.type)}
                              <span className="text-xs font-medium truncate flex-1" style={{ color: '#163329' }}>
                                {attachment.filename || `Attachment ${attIdx + 1}`}
                              </span>
                              {attachment.size_bytes > 0 && (
                                <span className="text-xs" style={{ color: '#888' }}>
                                  {formatFileSize(attachment.size_bytes)}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-stretch gap-2">
                              {/* View Button */}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="flex-1 basis-[6rem] min-w-[6rem] h-8 text-xs px-3 gap-1.5 whitespace-nowrap"
                                onClick={() => handleViewAttachment(attachment)}
                                data-testid={`view-attachment-${announcement.id}-${attIdx}`}
                              >
                                <Eye className="h-3.5 w-3.5 shrink-0" />
                                View
                              </Button>
                              {/* Download Button */}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="flex-1 basis-[6rem] min-w-[6rem] h-8 text-xs px-3 gap-1.5 whitespace-nowrap"
                                onClick={() => handleDownloadAttachment(attachment)}
                                data-testid={`download-attachment-${announcement.id}-${attIdx}`}
                              >
                                <Download className="h-3.5 w-3.5 shrink-0" />
                                Download
                              </Button>
                              {/* Replace Button */}
                              <div className="flex-1 basis-[6rem] min-w-[6rem]">
                                <input
                                  type="file"
                                  accept="image/*,.pdf,audio/*,video/*"
                                  className="hidden"
                                  id={`replace-attachment-${announcement.id}-${attIdx}`}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleReplaceAttachment(announcement.id, attIdx, file);
                                    }
                                    e.target.value = '';
                                  }}
                                  data-testid={`replace-input-${announcement.id}-${attIdx}`}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="w-full h-7 text-xs px-1.5 gap-0.5"
                                  disabled={replacingAttachmentIndex === attIdx}
                                  onClick={() => document.getElementById(`replace-attachment-${announcement.id}-${attIdx}`)?.click()}
                                  data-testid={`replace-attachment-${announcement.id}-${attIdx}`}
                                >
                                  {replacingAttachmentIndex === attIdx ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <>
                                      <RefreshCw className="h-3 w-3 mr-0" />
                                      Replace
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {announcement.link_url && (
                    <div className="flex items-center gap-1 text-xs mb-3" style={{ color: '#5C635A' }}>
                      <Link className="h-3 w-3" />
                      <span className="truncate">{announcement.link_url}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                    <span className="text-xs" style={{ color: '#888' }}>
                      {new Date(announcement.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(announcement)}
                        data-testid={`edit-announcement-${announcement.id}`}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(announcement.id)}
                        data-testid={`delete-announcement-${announcement.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Edit Announcement
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="edit-announcement-title-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="edit-announcement-description-input"
                />
              </div>
              
              <FilePickerSection
                isEdit={true}
                pendingFiles={pendingFiles}
                existingAttachments={existingAttachments}
                onFilesChange={handleFilesChange}
                onRemovePendingFile={removePendingFile}
                onRemoveExistingAttachment={removeExistingAttachment}
              />
              
              <div>
                <Label htmlFor="edit-link">Link URL (optional)</Label>
                <Input
                  id="edit-link"
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="edit-announcement-link-input"
                />
              </div>
              
              {uploadProgress && (
                <div className="p-2 rounded bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {uploadProgress}
                </div>
              )}
              
              <p className="text-sm p-2 rounded" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                Note: Editing an approved announcement will reset it to pending status for re-approval.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowEdit(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.title}
                  style={{ backgroundColor: '#163329' }}
                  data-testid="update-announcement-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Announcement'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeacherAnnouncementsPage;
